import React, { useEffect, useMemo, useState } from "react";
import { nexusEventLabel } from "./nexusEventLabels";
import {
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Tabs,
  Tooltip,
  message,
} from "antd";
import { EyeOutlined, LeftOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  ButtonFilled,
  ButtonOutlined,
  Buttontransparent,
} from "../../Reusablecomponents/UIButtons";
import { ReactComponent as EditIcon } from "../../assets/images/Editicon.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete-icon.svg";
import { ReactComponent as Swap } from "../../assets/images/swap-alt-svgrepo-com.svg";
import { ReactComponent as UpdateCompleted } from "../../assets/images/update-complete.svg";
import { ReactComponent as MStart } from "../../assets/images/MStart.svg";
import { StyledTable } from "../../Reusablecomponents/UITable/UITable";
import UIModal from "../../Reusablecomponents/UIModal";
import MapComponent from "../../Reusablecomponents/MapComponent";
import FilesTable from "../../Reusablecomponents/FilesTable";
import ChatComponent from "../../Reusablecomponents/Comment/ChatComponent";
import { CALL_API } from "../../Common/api";
import {
  chargetype,
  DispatchType,
  FinishType,
  NexusFinishType,
  shipmentType,
  TripTypeEnum,
  PanTripTypeEnum,
} from "../../Common/constant";
import { ReactComponent as MapIcon } from "../../assets/images/Group 1000004561.svg";
import {
  NexusDetailsWrapper,
  DetailsGrid,
  FileUploadButton,
  enumName,
  fenceNames,
  fetchNexusTrackDetails,
  firstValue,
  formatNairobiTime,
  lowerKeys,
  nexusStatus,
  toMapEvents,
} from "./nexusConsignDetails";
import { TRAKING_API_BASE } from "../../Common/traking";

const NEXUS_TRIP_REFTAB = "NEXUSTRIP";
const ASSET_API_BASE =
  `${TRAKING_API_BASE}/traking`;
const ASSET_API_HEADERS = {
  "x-api-key": "trackright-secure-api-key-123",
  "Content-Type": "application/json",
};

const toNumberOrUndefined = (value) =>
  value === undefined || value === null || value === "" || Number.isNaN(Number(value))
    ? undefined
    : Number(value);

// stored UTC → dayjs in the browser's zone (DatePicker works in local time)
const toDayjs = (value) => (value && dayjs(value).isValid() ? dayjs(value) : undefined);

const NexusTripDetails = ({
  tripRecord,
  setConsignee,
  onTripChanged,
  deleteTrip,
  deleteConsign,
}) => {
  const navigate = useNavigate();
  const userInfo = useSelector((state) => state?.authTracking?.userInfo);
  const nexusFenceList = useSelector(
    (state) => state?.authTracking?.nexusFenceList,
  );
  const transporterList = useSelector(
    (state) => state?.authTracking?.transporterList,
  );
  const reduxPermissions = useSelector(
    (state) => state?.authTracking?.myPermissionList || {},
  );
  const tripPermission = (action) =>
    Array.isArray(reduxPermissions?.TRIPAI) &&
    reduxPermissions.TRIPAI.includes(action);
  const fileUpAllowed = useMemo(() => tripPermission("UF"), [reduxPermissions]);
  const updateAllowed = useMemo(() => tripPermission("ET"), [reduxPermissions]);
  const deleteAllowed = useMemo(() => tripPermission("DT"), [reduxPermissions]);
  // same permission as Start / Complete Trip Manually in TrackRight AI
  const manTripAllowed = useMemo(() => tripPermission("MS"), [reduxPermissions]);
  const consignDeleteAllowed = useMemo(
    () =>
      Array.isArray(reduxPermissions?.CONSIGNAI) &&
      reduxPermissions.CONSIGNAI.includes("DC"),
    [reduxPermissions],
  );

  const raw = lowerKeys(tripRecord?.rawTrip || tripRecord || {});
  const tripId = firstValue(tripRecord?.TRIPID, raw.tripid);

  const [activeKey, setActiveKey] = useState("1");
  const [loading, setLoading] = useState(false);
  const [trackData, setTrackData] = useState({
    trip: {},
    consignments: [],
    events: [],
  });
  const [filesList, setFilesList] = useState([]);
  const [commentsList, setCommentsList] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [isDeleteConsignOpen, setIsDeleteConsignOpen] = useState(false);
  const [deleteConsignId, setDeleteConsignId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [assetOptions, setAssetOptions] = useState([]);
  const [editForm] = Form.useForm();

  const loadTrackDetails = async () => {
    setLoading(true);
    try {
      setTrackData(await fetchNexusTrackDetails(tripId, userInfo?.MCID));
    } catch (error) {
      console.error("[NexusTripDetails] track details error:", error);
      setTrackData({ trip: {}, consignments: [], events: [] });
    } finally {
      setLoading(false);
    }
  };

  const getFileData = async () => {
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "ai/trip/getFileTab", {
        refTab: NEXUS_TRIP_REFTAB,
        refId: tripId,
      });
      setFilesList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[NexusTripDetails] files error:", error);
      setFilesList([]);
    } finally {
      setLoading(false);
    }
  };

  const getCommentData = async () => {
    setLoading(true);
    try {
      const { data } = await CALL_API("POST", "ai/trip/getcommentTab", {
        refTab: NEXUS_TRIP_REFTAB,
        refId: tripId,
      });
      setCommentsList(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[NexusTripDetails] comments error:", error);
      setCommentsList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTrackDetails();
  }, [tripId]);

  useEffect(() => {
    if (activeKey === "4") getFileData();
    if (activeKey === "5") getCommentData();
  }, [activeKey]);

  const { consignments, events } = trackData;
  // nexusTrip row from the API, falling back to the trip list row
  const trip = { ...raw, ...trackData.trip };
  const first = consignments[0] || {};
  // 1 = Delivery, 2 = Collection: the events are named differently
  const shipType = firstValue(trip.shipmenttype, first.shipmenttype);
  const mapEvents = useMemo(() => toMapEvents(events, shipType), [events, shipType]);

  const consignorName = (c) =>
    firstValue(c.consignorname, fenceNames(nexusFenceList, c.consigner));
  const consigneeName = (c) =>
    firstValue(c.consigneename, fenceNames(nexusFenceList, c.consignee));
  const transporterName = (id) =>
    firstValue(
      transporterList?.find((t) => String(t.ID) === String(id))?.Name,
      id,
    );

  const status = nexusStatus(trip.status);
  const tripTypeList = userInfo?.MCID === 72 ? PanTripTypeEnum : TripTypeEnum;
  // A consignment is completed once its drop was reached (its own
  // REACHED_DELIVERY_POINT event), or when the whole trip is finished.
  const reachedEvents = (events || []).filter(
    (ev) => String(ev.eventname || "").toUpperCase() === "REACHED_DELIVERY_POINT",
  );
  const reachedConsignIds = new Set(
    reachedEvents.filter((ev) => ev.consignid != null).map((ev) => String(ev.consignid)),
  );
  const reachedDropSeqs = new Set(
    reachedEvents
      .filter((ev) => ev.consignid == null && ev.dropseq != null)
      .map((ev) => String(ev.dropseq)),
  );
  const byConsignId = consignments.filter(
    (c) => Number(c.status) === 4 || reachedConsignIds.has(String(c.consignid)),
  ).length;
  const completedCount =
    Number(trip.status) === 4
      ? consignments.length
      : Math.min(consignments.length, Math.max(byConsignId, reachedDropSeqs.size));
  const consignees = [
    ...new Set(consignments.map(consigneeName).filter(Boolean)),
  ];
  const containers = [
    ...new Set(consignments.map((c) => c.containerno).filter(Boolean)),
  ];

  // ─── Edit / Delete (nexus/updatetrip, nexus/deletetrip) ───────────────────

  const loadAssets = async (companyId) => {
    if (!companyId) return setAssetOptions([]);
    try {
      const response = await fetch(
        `${ASSET_API_BASE}/allAsset?company_id=${companyId}`,
        { method: "GET", headers: ASSET_API_HEADERS },
      );
      const data = await response.json();
      setAssetOptions(
        (Array.isArray(data?.response) ? data.response : []).map((a) => ({
          value: a.asset_id,
          label: a.asset_name,
          asset: a,
        })),
      );
    } catch (error) {
      console.error("[NexusTripDetails] allAsset error:", error);
      setAssetOptions([]);
    }
  };

  const openEdit = () => {
    loadAssets(firstValue(first.companyid, trip.companyid));
    editForm.setFieldsValue({
      vehid: toNumberOrUndefined(firstValue(trip.vehid, first.vehid)),
      vehicleName: firstValue(trip.vehiclename, first.vehiclename),
      deviceId: firstValue(trip.device, first.deviceid),
      driverId: firstValue(trip.driverid, first.driverid),
      driverName: firstValue(trip.drivername, first.drivername),
      deliveryNo: firstValue(trip.deliveryno, first.deliveryno),
      startTime: toDayjs(firstValue(trip.starttime, first.pickuptime)),
      dropTime: toDayjs(first.droptime),
      tripType: toNumberOrUndefined(firstValue(trip.triptype, first.triptype)),
      shipmentType: toNumberOrUndefined(
        firstValue(trip.shipmenttype, first.shipmenttype),
      ),
      // DispatchType option values are strings ("0"/"1"), unlike the other
      // enum fields here, so this one must not be coerced to a number.
      dispatchType: (() => {
        const v = firstValue(trip.dispatchtype, first.dispatchtype);
        return v === undefined || v === null || v === "" ? undefined : String(v);
      })(),
      completionType: toNumberOrUndefined(
        firstValue(trip.completiontype, first.completiontype),
      ),
      odoStart: trip.odostart ?? undefined,
      odoEnd: trip.odoent ?? undefined,
      tripallowance:
        trip.tripallowance != null && trip.tripallowance !== "" ? Number(trip.tripallowance) : undefined,
    });
    setIsEditOpen(true);
  };

  // Vehicle picked → name, device and driver from the Asset API
  const handleVehicleSelect = async (assetId) => {
    const selected = assetOptions.find((o) => o.value === assetId)?.asset || {};
    editForm.setFieldsValue({
      vehicleName: selected.asset_name,
      deviceId: selected.device_imei || undefined,
      driverId: selected.master_driver_id ?? undefined,
      driverName: selected.master_driver_name || undefined,
    });
    try {
      const response = await fetch(
        `${ASSET_API_BASE}/getInfoByAssetId?asset_id=${assetId}`,
        { method: "GET", headers: ASSET_API_HEADERS },
      );
      const info = (await response.json())?.response || {};
      editForm.setFieldsValue({
        vehicleName: info.asset_name || selected.asset_name,
        deviceId: info.imei || selected.device_imei || undefined,
      });
    } catch (error) {
      console.error("[NexusTripDetails] getInfoByAssetId error:", error);
    }
  };

  const handleSaveEdit = async (values) => {
    setSaving(true);
    try {
      const res = await CALL_API("PUT", "nexus/updatetrip", {
        tripid: tripId,
        vehid: values.vehid ?? null,
        vehicleName: values.vehicleName ?? null,
        deviceId: values.deviceId ?? null,
        driverId: values.driverId ?? null,
        driverName: values.driverName ?? null,
        deliveryNo: values.deliveryNo ?? null,
        startTime: values.startTime ? values.startTime.toISOString() : null,
        dropTime: values.dropTime ? values.dropTime.toISOString() : null,
        tripType: values.tripType ?? null,
        shipmentType: values.shipmentType ?? null,
        dispatchType: values.dispatchType ?? null,
        completionType: values.completionType ?? null,
        odoStart: values.odoStart ?? null,
        odoEnd: values.odoEnd ?? null,
        tripallowance: values.tripallowance ?? null,
      });
      if (res?.ResponseCode === 200) {
        message.success("Trip updated successfully");
        setIsEditOpen(false);
        await loadTrackDetails();
        onTripChanged?.();
      } else {
        message.error(res?.message || res?.data || "Failed to update trip");
      }
    } finally {
      setSaving(false);
    }
  };

  // Start (not started) or complete (in progress) the trip by hand. The backend
  // refuses anything else, so a trip cannot be started or completed twice.
  const handleManualAction = async () => {
    const action = Number(trip.status) === 3 ? "END" : "START";
    setSaving(true);
    try {
      const res = await CALL_API("POST", "nexus/manualtripaction", { tripId, action });
      if (res?.ResponseCode === 200) {
        message.success(action === "END" ? "Trip completed successfully" : "Trip started successfully");
        setIsManualOpen(false);
        await loadTrackDetails(); // new status and the Started / Finished event
        onTripChanged?.(); // refresh the trip list
      } else {
        message.warning(res?.data || res?.message || "Unable to update the trip");
        setIsManualOpen(false);
        loadTrackDetails();
      }
    } catch (error) {
      console.error("[NexusTripDetails] manual action error:", error);
      message.error("Unable to update the trip");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTrip = async () => {
    setSaving(true);
    try {
      // nexus/deletetrip also deletes the TraKing trip (by FWayBill)
      const result = deleteTrip
        ? await deleteTrip(tripId)
        : await CALL_API("DELETE", `nexus/deletetrip?tripId=${tripId}`).then(
            (res) => ({ ok: res?.ResponseCode === 200, data: res }),
          );
      if (result.ok) {
        message.success("Trip deleted successfully");
        setIsDeleteOpen(false);
        onTripChanged?.();
        setConsignee(false);
      } else {
        message.error(
          result.data?.message || result.data?.data || "Failed to delete trip",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConsign = async () => {
    if (!deleteConsignId) return;
    setSaving(true);
    try {
      const result = deleteConsign
        ? await deleteConsign(deleteConsignId)
        : await CALL_API(
            "DELETE",
            `nexus/deleteconsign?id=${deleteConsignId}`,
          ).then((res) => ({ ok: res?.ResponseCode === 200, data: res }));
      if (result.ok) {
        message.success("Consignment deleted successfully");
        setIsDeleteConsignOpen(false);
        setDeleteConsignId(null);
        loadTrackDetails();
      } else {
        message.error(
          result.data?.message || result.data?.data || "Failed to delete consignment",
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const detailItems = [
    { label: "Driver Name", value: firstValue(trip.drivername, first.drivername) },
    { label: "Vehicle", value: firstValue(trip.vehiclename, first.vehiclename) },
    { label: "Device", value: firstValue(trip.device, first.deviceid) },
    {
      label: "Consign",
      value: firstValue(consignments.length || undefined, trip.consigncount),
    },
    { label: "Completed Consignment", value: String(completedCount) },
    { label: "Consignor", value: consignorName(first) },
    { label: "Consignee", value: consignees.join(", ") },
    { label: "Transporter", value: transporterName(first.transporter) },
    { label: "Charge Type", value: enumName(chargetype, first.chargetype) },
    {
      label: "Trip Type",
      value: enumName(tripTypeList, firstValue(trip.triptype, first.triptype)),
    },
    {
      label: "Shipment Type",
      value: enumName(shipmentType, firstValue(trip.shipmenttype, first.shipmenttype)),
    },
    {
      label: "Dispatch Type",
      value: enumName(DispatchType, firstValue(trip.dispatchtype, first.dispatchtype)),
    },
    {
      label: "Finish Type",
      value: enumName(FinishType, firstValue(trip.completiontype, first.completiontype)),
    },
    {
      label: "Start Time",
      value: formatNairobiTime(firstValue(trip.starttime, first.pickuptime)),
    },
    { label: "ETA", value: formatNairobiTime(first.droptime) },
    {
      label: "ODO Kms",
      // telematics distance saved when the trip finished; manual ODO readings
      // are the fallback
      value:
        trip.gpskms != null && trip.gpskms !== "" && Number(trip.gpskms) > 0
          ? `${Number(trip.gpskms).toFixed(2)} Kms`
          : trip.odostart != null && trip.odoent != null
            ? `${Number(trip.odoent) - Number(trip.odostart)} Kms`
            : undefined,
    },
    {
      label: "Trip Allowance",
      value:
        trip.tripallowance != null && trip.tripallowance !== ""
          ? Number(trip.tripallowance).toLocaleString("en-US")
          : undefined,
    },
    {
      label: "Carbon Footprint",
      value: trip.carbonfootprint ? `${trip.carbonfootprint} KgCO₂` : undefined,
    },
    {
      label: "Delivery Note Number",
      value: firstValue(trip.deliveryno, first.deliveryno),
    },
    { label: "Container No", value: containers.join(", ") },
    { label: "Waybill", value: firstValue(trip.fwaybill, first.fwaybill) },
    { label: "Created On", value: formatNairobiTime(trip.cron) },
  ];

  // Latest event of one consignment. `events` is in the order they happened. Events
  // recorded for the consignment itself win (e.g. Reached Delivery Point of the 1st
  // drop); otherwise the trip-level ones apply (e.g. Delivery Journey Started on
  // the way to the 2nd drop). A finished trip shows its last event for all.
  const latestEventOf = (consignId) => {
    const list = events || [];
    if (!list.length) return null;
    if (Number(trip.status) === 4) return list[list.length - 1];
    const own = list.filter((ev) => ev.consignid != null && String(ev.consignid) === String(consignId));
    if (own.length) return own[own.length - 1];
    const shared = list.filter((ev) => ev.consignid == null);
    return shared.length ? shared[shared.length - 1] : null;
  };

  const consignColumns = [
    { title: "Consign Code", dataIndex: "code", key: "code" },
    {
      title: "Event",
      dataIndex: "status",
      key: "status",
      render: (value, record) => {
        const latest = latestEventOf(record.consignid);
        if (latest) return <span>{nexusEventLabel(latest.eventname, shipType)}</span>;
        // no event yet: the consignment's own status
        const s = nexusStatus(value);
        return <span style={{ color: s.color }}>{s.label}</span>;
      },
    },
    {
      title: "Consignor",
      key: "consignor",
      render: (_, record) => consignorName(record) || "---",
    },
    {
      title: "Consignee",
      key: "consignee",
      render: (_, record) => consigneeName(record) || "---",
    },
    {
      title: "Transporter",
      key: "transporter",
      render: (_, record) => transporterName(record.transporter) || "---",
    },
    {
      title: "Container No",
      dataIndex: "containerno",
      key: "containerno",
      render: (text) => text || "---",
    },
    {
      title: "Delivery No",
      dataIndex: "deliveryno",
      key: "deliveryno",
      render: (text) => text || "---",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="d-flex gap-5" style={{ display: "flex", gap: 10 }}>
          <EyeOutlined
            style={{ cursor: "pointer", fontSize: "17px" }}
            onClick={(e) => {
              e.stopPropagation();
              navigate("/nexusConsignAI", {
                state: {
                  consignmentRecord: {
                    ConsignId: record.consignid,
                    tripId,
                    rawTrip: { ...record, tripid: tripId },
                  },
                },
              });
            }}
          />
          <Tooltip title={!consignDeleteAllowed ? "No permission" : ""}>
            <DeleteIcon
              style={{
                cursor: consignDeleteAllowed ? "pointer" : "not-allowed",
              }}
              onClick={(e) => {
                if (!consignDeleteAllowed) return;
                e.stopPropagation();
                setDeleteConsignId(record.consignid);
                setIsDeleteConsignOpen(true);
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const eventColumns = [
    {
      title: "Event",
      dataIndex: "eventname",
      key: "eventname",
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          {record.lat && record.lng && (
            <MapIcon
              style={{ width: "16px", height: "16px", cursor: "pointer" }}
              onClick={() => setSelectedEvent(record)}
            />
          )}
          <span>{nexusEventLabel(text, shipType) || "---"}</span>
        </div>
      ),
    },
    {
      title: "Consignee",
      key: "consignee",
      render: (_, record) => {
        const c = consignments.find(
          (row) => String(row.consignid) === String(record.consignid),
        );
        return (c && consigneeName(c)) || "---";
      },
    },
    {
      title: "Location",
      dataIndex: "fencename",
      key: "fencename",
      render: (text, record) => (
        // inline spans, not stacked <p> tags: the stacked version rendered the
        // fence name and "Drop N" on top of each other in this table
        <span>
          {text || "---"}
          {record.dropseq != null && (
            <span style={{ color: "#979797", fontSize: 14, marginLeft: 8 }}>
              Drop {record.dropseq}
            </span>
          )}
        </span>
      ),
    },
    {
      title: "Event Time",
      dataIndex: "eventtime",
      key: "eventtime",
      render: (value) => formatNairobiTime(value),
    },
  ];

  return (
    <NexusDetailsWrapper>
      <Row gutter={16}>
        <Col xs={24}>
          <div className="details_header">
            <div className="details_header_left">
              <ButtonOutlined
                onClick={() => setConsignee(false)}
                className="back_btn"
              >
                <LeftOutlined />
              </ButtonOutlined>
              <p>Trip Code - {firstValue(trip.tripcode, first.code) || "---"}</p>
              <span className="status_pill" style={{ color: status.color }}>
                <span
                  className="status_dot"
                  style={{ backgroundColor: status.color }}
                />
                {status.label}
              </span>
            </div>
            <div className="details_header_right">
              {[0, 1].includes(Number(trip.status ?? 0)) && (
                <Tooltip title={!manTripAllowed ? "No permission" : ""}>
                  <ButtonOutlined
                    className="bulk_btn"
                    style={{ cursor: manTripAllowed ? "pointer" : "not-allowed" }}
                    onClick={() => manTripAllowed && setIsManualOpen(true)}
                  >
                    <Swap />
                    Start Trip Manually
                  </ButtonOutlined>
                </Tooltip>
              )}
              {Number(trip.status) === 3 && (
                <Tooltip title={!manTripAllowed ? "No permission" : ""}>
                  <ButtonOutlined
                    className="update_complete_btn"
                    style={{ cursor: manTripAllowed ? "pointer" : "not-allowed" }}
                    onClick={() => manTripAllowed && setIsManualOpen(true)}
                  >
                    <UpdateCompleted />
                    Complete Trip Manually
                  </ButtonOutlined>
                </Tooltip>
              )}
              <Tooltip title={!updateAllowed ? "No permission" : ""}>
                <ButtonOutlined className="icon_btn">
                  <EditIcon
                    style={{ cursor: updateAllowed ? "pointer" : "not-allowed" }}
                    onClick={() => updateAllowed && openEdit()}
                  />
                </ButtonOutlined>
              </Tooltip>
              <Tooltip title={!deleteAllowed ? "No permission" : ""}>
                <ButtonOutlined className="icon_btn">
                  <DeleteIcon
                    style={{ cursor: deleteAllowed ? "pointer" : "not-allowed" }}
                    onClick={() => deleteAllowed && setIsDeleteOpen(true)}
                  />
                </ButtonOutlined>
              </Tooltip>
            </div>
          </div>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <DetailsGrid items={detailItems} />

        <div className="tab_row">
          <Tabs
            className="nexus_tabs"
            type="card"
            activeKey={activeKey}
            onChange={setActiveKey}
            items={[
              { label: "Consignment", key: "1" },
              { label: "Map", key: "2" },
              { label: "Event", key: "3" },
              { label: "Files", key: "4" },
              { label: "Comment", key: "5" },
            ]}
          />
          {activeKey === "4" && (
            <FileUploadButton
              refTab={NEXUS_TRIP_REFTAB}
              refId={tripId}
              onUploaded={getFileData}
              disabled={!fileUpAllowed}
            />
          )}
        </div>

        {activeKey === "1" && (
          <StyledTable
            dataSource={consignments.map((c, i) => ({ ...c, key: c.consignid ?? i }))}
            columns={consignColumns}
            pagination={false}
          />
        )}
        {activeKey === "2" && (
          <MapComponent
            locationsTLAI={mapEvents}
            LocationName={mapEvents}
            status={Number(trip.status)}
          />
        )}
        {activeKey === "3" && (
          <StyledTable
            dataSource={events.map((ev, i) => ({ ...ev, key: ev.id ?? i }))}
            columns={eventColumns}
            pagination={false}
          />
        )}
        {activeKey === "4" && (
          <FilesTable filesList={filesList} getFileData={getFileData} />
        )}
        {activeKey === "5" && (
          <ChatComponent
            data={commentsList}
            user={{ UID: userInfo?.UID }}
            getData={getCommentData}
            id={tripId}
            RefTab={NEXUS_TRIP_REFTAB}
            setLoading={setLoading}
          />
        )}
      </Spin>

      <UIModal
        customHeight={500}
        open={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
        closeButton={false}
      >
        {selectedEvent && (
          <MapComponent
            locationsTL={[
              {
                EventName: nexusEventLabel(selectedEvent.eventname, shipType),
                Lat: Number(selectedEvent.lat),
                Lng: Number(selectedEvent.lng),
                MODON: selectedEvent.eventtime,
              },
            ]}
            pinlocation={[
              { lat: Number(selectedEvent.lat), lng: Number(selectedEvent.lng) },
            ]}
            isminimap={true}
          />
        )}
      </UIModal>

      <UIModal
        width={800}
        open={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        closeButton={false}
        header={<h4 style={{ margin: 0 }}>Modifying Trip</h4>}
        footer={
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "15px" }}>
            <Buttontransparent onClick={() => setIsEditOpen(false)}>
              Cancel
            </Buttontransparent>
            <ButtonFilled loading={saving} onClick={() => editForm.submit()}>
              Update
            </ButtonFilled>
          </div>
        }
      >
        <Form form={editForm} layout="vertical" onFinish={handleSaveEdit}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="vehid"
                label="Vehicle"
                rules={[{ required: true, message: "Vehicle is required" }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={assetOptions.map(({ value, label }) => ({ value, label }))}
                  onChange={handleVehicleSelect}
                  placeholder="Select vehicle"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="deviceId" label="Device">
                <Input disabled placeholder="Auto-filled from vehicle" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="driverName" label="Driver Name">
                <Input placeholder="Driver name" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="deliveryNo" label="Delivery Note Number">
                <Input placeholder="Delivery Note Number" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="startTime" label="Start Time">
                <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="dropTime" label="ETA">
                <DatePicker showTime format="DD/MM/YYYY HH:mm" style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="tripType" label="Trip Type">
                <Select
                  allowClear
                  options={tripTypeList?.map((i) => ({ label: i.name, value: i.value }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="shipmentType" label="Shipment Type">
                <Select
                  allowClear
                  options={shipmentType?.map((i) => ({ label: i.name, value: i.value }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="dispatchType" label="Dispatch Type">
                <Select
                  allowClear
                  options={DispatchType?.map((i) => ({ label: i.name, value: i.value }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="completionType" label="Finish Type">
                <Select
                  allowClear
                  options={NexusFinishType?.map((i) => ({ label: i.name, value: i.value }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="odoStart" label="ODO Start">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="tripallowance" label="Trip Allowance">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="odoEnd"
                label="ODO End"
                dependencies={["odoStart"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      const start = getFieldValue("odoStart");
                      if (value == null || start == null || value >= start) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("ODO End must be greater than or equal to ODO Start"),
                      );
                    },
                  }),
                ]}
              >
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          {/* not edited directly, but sent with the update */}
          <Form.Item name="vehicleName" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="driverId" hidden>
            <Input />
          </Form.Item>
        </Form>
      </UIModal>

      <UIModal
        width={500}
        open={isManualOpen}
        onClose={() => setIsManualOpen(false)}
        closeButton={false}
        size="md"
        header={false}
        footer={false}
      >
        <div className="text-center">
          <center>
            {Number(trip.status) === 3 ? <UpdateCompleted /> : <MStart />}
          </center>
          <br />
          <h4>{Number(trip.status) === 3 ? "Complete" : "Start"} Trip Manually</h4>
          <br />
          <p style={{ color: "#5F6D8C" }}>
            Are you sure you want to {Number(trip.status) === 3 ? "complete" : "start"} this trip
            manually?
          </p>
          <div className="mt-2 w-100 d-flex justify-content-center">
            <Buttontransparent onClick={() => setIsManualOpen(false)}>Cancel</Buttontransparent>
            <ButtonFilled loading={saving} onClick={handleManualAction}>
              Confirm
            </ButtonFilled>
          </div>
        </div>
      </UIModal>

      <UIModal
        width={500}
        open={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        closeButton={false}
        size="md"
        header={false}
        footer={false}
      >
        <div className="text-center">
          <h1>Are you sure,</h1>
          <h6>
            You want to delete this trip? All of its consignments will be
            deleted too.
          </h6>
          <div className="mt-2 w-100 d-flex justify-content-center">
            <ButtonFilled loading={saving} onClick={handleDeleteTrip}>
              Yes
            </ButtonFilled>
            <Buttontransparent onClick={() => setIsDeleteOpen(false)}>
              No
            </Buttontransparent>
          </div>
        </div>
      </UIModal>

      <UIModal
        width={500}
        open={isDeleteConsignOpen}
        onClose={() => setIsDeleteConsignOpen(false)}
        closeButton={false}
        size="md"
        header={false}
        footer={false}
      >
        <div className="text-center">
          <h1>Are you sure,</h1>
          <h6>You want to delete this consignment?</h6>
          <div className="mt-2 w-100 d-flex justify-content-center">
            <ButtonFilled loading={saving} onClick={handleDeleteConsign}>
              Yes
            </ButtonFilled>
            <Buttontransparent onClick={() => setIsDeleteConsignOpen(false)}>
              No
            </Buttontransparent>
          </div>
        </div>
      </UIModal>
    </NexusDetailsWrapper>
  );
};

export default NexusTripDetails;
